// mds-dojo — dojo/_base/Deferred — FAITHFUL port of references/dojo/_base/Deferred.js
// (the legacy synchronous Deferred API). The only omission is the
// has("config-useDeferredInstrumentation") NewDeferred.instrumentRejected hooks (that
// flag is off), so the `../Deferred` import is dropped. then/resolve(callback)/
// reject(errback)/progress/cancel/addCallback/addErrback/addBoth/isResolved/
// isRejected/isFulfilled/isCanceled + .promise + .fired/.results all faithful.
import dojo from './kernel';
import Promise from '../promise/Promise';
import CancelError from '../errors/CancelError';
import lang from './lang';
import when from '../when';

const mutator = function () { /* sentinel */ };
const freeze = Object.freeze || function () { /* no-op */ };

const Deferred: any = dojo.Deferred = function (this: any, canceller?: any) {
  let result: any, finished: any, canceled: any, fired: any, isError: any, head: any, nextListener: any;
  const promise = (this.promise = new (Promise as any)());
  const self = this;

  function complete(value: any) {
    if (finished) {
      throw new Error('This deferred has already been resolved');
    }
    result = value;
    finished = true;
    notify();
  }
  function notify() {
    let mutated: any;
    while (!mutated && nextListener) {
      const listener = nextListener;
      nextListener = nextListener.next;
      if ((mutated = (listener.progress == mutator))) {
        finished = false;
      }

      const func = (isError ? listener.error : listener.resolved);
      if (func) {
        try {
          const newResult = func(result);
          if (newResult && typeof newResult.then === 'function') {
            newResult.then(lang.hitch(listener.deferred, 'resolve'), lang.hitch(listener.deferred, 'reject'), lang.hitch(listener.deferred, 'progress'));
            continue;
          }
          const unchanged = mutated && newResult === undefined;
          if (mutated && !unchanged) {
            isError = newResult instanceof Error;
          }
          listener.deferred[unchanged && isError ? 'reject' : 'resolve'](unchanged ? result : newResult);
        } catch (e) {
          listener.deferred.reject(e);
        }
      } else {
        if (isError) {
          listener.deferred.reject(result);
        } else {
          listener.deferred.resolve(result);
        }
      }
    }
  }

  this.isResolved = promise.isResolved = function () { return fired == 0; };
  this.isRejected = promise.isRejected = function () { return fired == 1; };
  this.isFulfilled = promise.isFulfilled = function () { return fired >= 0; };
  this.isCanceled = promise.isCanceled = function () { return canceled; };

  this.resolve = this.callback = function (this: any, value: any) {
    this.fired = fired = 0;
    this.results = [value, null];
    complete(value);
  };

  this.reject = this.errback = function (this: any, error: any) {
    isError = true;
    this.fired = fired = 1;
    complete(error);
    this.results = [null, error];
  };

  this.progress = function (update: any) {
    let listener = nextListener;
    while (listener) {
      const progress = listener.progress;
      if (progress) { progress(update); }
      listener = listener.next;
    }
  };

  this.addCallbacks = function (this: any, callback: any, errback: any) {
    this.then(callback, errback, mutator);
    return this;
  };

  promise.then = this.then = function (resolvedCallback?: any, errorCallback?: any, progressCallback?: any) {
    const returnDeferred = progressCallback == mutator ? self : new (Deferred as any)(promise.cancel);
    const listener = {
      resolved: resolvedCallback,
      error: errorCallback,
      progress: progressCallback,
      deferred: returnDeferred,
      next: null as any,
    };
    if (nextListener) {
      head = head.next = listener;
    } else {
      nextListener = head = listener;
    }
    if (finished) {
      notify();
    }
    return returnDeferred.promise;
  };

  const deferred = this;
  promise.cancel = this.cancel = function () {
    if (!finished) {
      let error = canceller && canceller(deferred);
      if (!finished) {
        if (!(error instanceof Error)) {
          error = new (CancelError as any)(error);
        }
        error.log = false;
        deferred.reject(error);
      }
    }
    canceled = true;
  };
  freeze(promise);
};

lang.extend(Deferred, {
  addCallback: function (this: any, callback: any) {
    return this.addCallbacks((lang.hitch as any).apply(dojo, arguments));
  },
  addErrback: function (this: any, errback: any) {
    return this.addCallbacks(null, (lang.hitch as any).apply(dojo, arguments));
  },
  addBoth: function (this: any, callback: any) {
    const enclosed = (lang.hitch as any).apply(dojo, arguments);
    return this.addCallbacks(enclosed, enclosed);
  },
  fired: -1,
});

Deferred.when = dojo.when = when;

export default Deferred;
